<admin-app-header></admin-app-header>

<div class="global-variables-page">
    <div class="container-fluid">
        <!-- Page Header -->
        <div class="page-header">
            <div class="page-header__title">
                <h1><i class="fa-solid fa-sliders"></i> Global Variables</h1>
                <p class="page-header__subtitle">Configure system-wide variables used for calculations and reporting</p>
            </div>
        </div>

        <!-- Loading State -->
        <div *ngIf="loading" class="loading-state">
            <i class="fa-solid fa-spinner fa-spin"></i>
            <span>Loading variables...</span>
        </div>

        <!-- Main Content -->
        <div *ngIf="!loading" class="content-grid">
            <!-- Settings Form -->
            <div class="settings-card">
                <div class="card-header">
                    <i class="fa-solid fa-cog"></i>
                    <h3>Settings</h3>
                </div>
                <div class="card-body">
                    <form [formGroup]="variablesForm" (ngSubmit)="onSubmit()">
                        <!-- Financial Section -->
                        <div class="form-section">
                            <h4 class="section-title">Financial</h4>

                            <div class="form-group">
                                <label for="profitPercentage">Profit Percentage</label>
                                <div class="input-with-addon">
                                    <input
                                        type="number"
                                        id="profitPercentage"
                                        class="form-control"
                                        formControlName="profitPercentage"
                                        min="0"
                                        max="100"
                                        step="0.5"
                                    />
                                    <span class="input-addon">%</span>
                                </div>
                                <small class="form-hint">Percentage of turnover that is profit</small>
                            </div>

                            <div class="form-group">
                                <label for="averageBayPrice">Average Bay Price</label>
                                <div class="input-with-addon">
                                    <span class="input-addon prefix">R</span>
                                    <input type="number" id="averageBayPrice" class="form-control with-prefix" formControlName="averageBayPrice" min="0" step="100" />
                                </div>
                                <small class="form-hint">Average monthly price per parking bay</small>
                            </div>
                        </div>

                        <!-- Gateway Fees Section -->
                        <div class="form-section">
                            <h4 class="section-title">Gateway Fees</h4>

                            <div class="form-group">
                                <label for="paygateGatewayFeePercentage">PayGate / PayFast Fee</label>
                                <div class="input-with-addon">
                                    <input
                                        type="number"
                                        id="paygateGatewayFeePercentage"
                                        class="form-control"
                                        formControlName="paygateGatewayFeePercentage"
                                        min="0"
                                        max="100"
                                        step="0.1"
                                    />
                                    <span class="input-addon">%</span>
                                </div>
                                <small class="form-hint">Gateway fee for card payments (PayGate/PayFast)</small>
                            </div>

                            <div class="form-group">
                                <label for="eftGatewayFeePercentage">EFT Fee</label>
                                <div class="input-with-addon">
                                    <input
                                        type="number"
                                        id="eftGatewayFeePercentage"
                                        class="form-control"
                                        formControlName="eftGatewayFeePercentage"
                                        min="0"
                                        max="100"
                                        step="0.1"
                                    />
                                    <span class="input-addon">%</span>
                                </div>
                                <small class="form-hint">Gateway fee for EFT payments</small>
                            </div>
                        </div>

                        <!-- Subscription Defaults Section -->
                        <div class="form-section">
                            <h4 class="section-title">Subscription Defaults</h4>

                            <div class="form-group">
                                <label for="averageMonths">Average Duration</label>
                                <div class="input-with-addon">
                                    <input type="number" id="averageMonths" class="form-control" formControlName="averageMonths" min="1" max="36" />
                                    <span class="input-addon">months</span>
                                </div>
                                <small class="form-hint">Average subscription length</small>
                            </div>

                            <div class="form-group">
                                <label for="averageBaysPerSub">Average Bays per Subscription</label>
                                <div class="input-with-addon">
                                    <input type="number" id="averageBaysPerSub" class="form-control" formControlName="averageBaysPerSub" min="1" max="100" />
                                    <span class="input-addon">bays</span>
                                </div>
                                <small class="form-hint">Average number of bays per subscription</small>
                            </div>
                        </div>

                        <div class="form-actions">
                            <button type="submit" class="btn btn-primary" [disabled]="variablesForm.invalid || saving">
                                <i *ngIf="saving" class="fa-solid fa-spinner fa-spin"></i>
                                <i *ngIf="!saving" class="fa-solid fa-save"></i>
                                {{ saving ? 'Saving...' : 'Save Changes' }}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            <!-- Preview Card -->
            <div class="preview-card">
                <div class="card-header">
                    <i class="fa-solid fa-calculator"></i>
                    <h3>Calculated Values</h3>
                </div>
                <div class="card-body">
                    <div class="preview-item">
                        <span class="preview-label">Average Subscription Value</span>
                        <span class="preview-value highlight">{{ averageSubscriptionValue | currency: 'R':'symbol':'1.0-0' }}</span>
                        <small class="preview-hint">{{ variablesForm.value.averageBaysPerSub }} bays × {{ variablesForm.value.averageMonths }} months × R{{ variablesForm.value.averageBayPrice }}</small>
                    </div>

                    <div class="preview-item">
                        <span class="preview-label">Profit per Subscription</span>
                        <span class="preview-value success">{{ profitPerSubscription | currency: 'R':'symbol':'1.0-0' }}</span>
                        <small class="preview-hint">{{ variablesForm.value.profitPercentage }}% of average value</small>
                    </div>

                    <div class="preview-divider"></div>

                    <h4 class="preview-section-title">Lead Value Thresholds</h4>
                    <p class="preview-description">Used to classify leads as High, Medium, or Low value in the CRM</p>

                    <div class="threshold-grid">
                        <div class="threshold-item threshold-high">
                            <span class="threshold-badge">HIGH</span>
                            <span class="threshold-value">≥ {{ highValueThreshold | currency: 'R':'symbol':'1.0-0' }}</span>
                            <small class="threshold-hint">2× average or more</small>
                        </div>
                        <div class="threshold-item threshold-medium">
                            <span class="threshold-badge">MEDIUM</span>
                            <span class="threshold-value">{{ lowValueThreshold | currency: 'R':'symbol':'1.0-0' }} - {{ highValueThreshold | currency: 'R':'symbol':'1.0-0' }}</span>
                            <small class="threshold-hint">Between thresholds</small>
                        </div>
                        <div class="threshold-item threshold-low">
                            <span class="threshold-badge">LOW</span>
                            <span class="threshold-value">≤ {{ lowValueThreshold | currency: 'R':'symbol':'1.0-0' }}</span>
                            <small class="threshold-hint">Half average or less</small>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</div>

